import { useState, useEffect, useRef, useCallback } from 'react';
import type { CoqManager } from 'jscoq';
import toast from 'react-hot-toast';
import type { ProofState } from '../types/proof';
import { parseJsCoqState, rawGoalsAt, sentenceMessages } from '../utils/jsCoqState';

export function useJsCoqState(coqManager: CoqManager | null, isLoaded: boolean, onStateChange?: () => void) {
  const [goalState, setGoalState] = useState<ProofState | null>(null);
  const notifiedProofsRef = useRef(new WeakSet<object>());

  const refreshState = useCallback(() => {
    const sentence = coqManager?.lastAdded?.();
    if (!coqManager || !sentence) return;
    const goals = rawGoalsAt(coqManager.doc, sentence.coq_sid);
    const messages = sentenceMessages(sentence, coqManager);
    if (goals === undefined && messages.length === 0) return;

    // Read all feedback for the focused sentence each time. Consuming a global
    // message queue loses Print/Check/Compute output when navigating back.
    const state = parseJsCoqState(goals, coqManager, messages);
    state.hasError = messages.some(message => message.level === 'error');
    setGoalState(state);
    onStateChange?.();
    if (state.provedTheorem && !state.hasError && !notifiedProofsRef.current.has(sentence)) {
      notifiedProofsRef.current.add(sentence);
      toast.success('Theorem proven: ' + state.provedTheorem);
    }
  }, [coqManager, onStateChange]);

  useEffect(() => {
    if (!coqManager || !isLoaded) return;
    let disposed = false;
    let refreshQueued = false;
    const scheduleRefresh = () => {
      if (refreshQueued || disposed) return;
      refreshQueued = true;
      queueMicrotask(() => {
        refreshQueued = false;
        if (!disposed) refreshState();
      });
    };

    const originalGoalInfo = coqManager.coqGoalInfo;
    const originalUpdateGoals = coqManager.updateGoals;
    const originalFeedMessage = coqManager.feedMessage;
    const originalRefreshGoals = coqManager.refreshGoals;

    coqManager.coqGoalInfo = function(sid: number, rawGoals: unknown) {
      // The native renderer reverses the first goal's hypotheses in place.
      // Keep an untouched worker snapshot for every displayed branch.
      this.doc.goals_raw ??= {};
      this.doc.goals_raw[sid] = structuredClone(rawGoals);
      const result = originalGoalInfo?.call(this, sid, rawGoals);
      // The native renderer ignores null, including after Print and Qed.
      if (this.lastAdded()?.coq_sid === sid) scheduleRefresh();
      return result;
    };
    coqManager.updateGoals = function(...args: Parameters<CoqManager['updateGoals']>) {
      const result = originalUpdateGoals.apply(this, args);
      scheduleRefresh();
      return result;
    };
    if (originalFeedMessage) {
      coqManager.feedMessage = function(...args: unknown[]) {
        const result = originalFeedMessage.apply(this, args);
        // Feedback may arrive before or after its command's goal response.
        if (this.lastAdded()?.coq_sid === args[0]) scheduleRefresh();
        return result;
      };
    }
    if (originalRefreshGoals) {
      coqManager.refreshGoals = function(...args: unknown[]) {
        const result = originalRefreshGoals.apply(this, args);
        // Backward navigation can reuse cached goals without executing a query.
        scheduleRefresh();
        return result;
      };
    }

    scheduleRefresh();
    return () => {
      disposed = true;
      coqManager.coqGoalInfo = originalGoalInfo;
      coqManager.updateGoals = originalUpdateGoals;
      if (originalFeedMessage) coqManager.feedMessage = originalFeedMessage;
      if (originalRefreshGoals) coqManager.refreshGoals = originalRefreshGoals;
    };
  }, [coqManager, isLoaded, refreshState]);

  const reset = useCallback(() => {
    setGoalState(null);
    notifiedProofsRef.current = new WeakSet();
  }, []);

  return { goalState, reset };
}

